import { useState, useEffect, useRef } from 'react';
import {
  MapPin,
  Flame,
  Star,
  Settings,
  ChevronRight,
  LogOut,
  Award,
  Calendar,
  TrendingUp,
  Loader2,
  Bookmark,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import SettingsPage from './SettingsPage';
import FriendsPageSimple from './FriendsPageSimple';
import SavedVenues from './SavedVenues';

interface ProfilePageProps {
  onClose: () => void;
}

interface UserStats {
  checkins: number;
  places: number;
  friends: number;
  streak: number;
  points: number;
  mayorships: number;
  weeklyCheckins: number;
}

interface Achievement {
  id: string;
  name: string;
  icon: string;
  earned: boolean;
  description: string;
}

export default function ProfilePage({ onClose }: ProfilePageProps) {
  const { user, signOut, profileAvatarUrl, refreshProfile } = useAuth();
  const [activeTab, setActiveTab] = useState<'stats' | 'friends' | 'achievements' | 'saved'>('stats');
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showFriends, setShowFriends] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [stats, setStats] = useState<UserStats>({
    checkins: 0,
    places: 0,
    friends: 1, // Matthew Robin is always a friend
    streak: 0,
    points: 0,
    mayorships: 0,
    weeklyCheckins: 0,
  });
  const [achievements, setAchievements] = useState<Achievement[]>([]);

  // Fetch real stats from Supabase
  useEffect(() => {
    const fetchUserStats = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        // Get total check-ins count
        const { count: totalCheckins } = await supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .is('parent_message_id', null)
          .eq('is_deleted', false);

        // Get unique places count
        const { data: placesData } = await supabase
          .from('messages')
          .select('venue_id')
          .eq('user_id', user.id)
          .is('parent_message_id', null)
          .eq('is_deleted', false);

        const uniquePlaces = new Set(placesData?.map((p) => p.venue_id) || []).size;

        // Get weekly check-ins (last 7 days)
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);

        const { count: weeklyCount } = await supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .is('parent_message_id', null)
          .eq('is_deleted', false)
          .gte('created_at', weekAgo.toISOString());

        // Calculate streak (consecutive days with check-ins)
        const { data: recentCheckins } = await supabase
          .from('messages')
          .select('created_at')
          .eq('user_id', user.id)
          .is('parent_message_id', null)
          .eq('is_deleted', false)
          .order('created_at', { ascending: false })
          .limit(100);

        let streak = 0;
        if (recentCheckins && recentCheckins.length > 0) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const checkinDays = new Set<string>();
          recentCheckins.forEach((c) => {
            const date = new Date(c.created_at);
            date.setHours(0, 0, 0, 0);
            checkinDays.add(date.toISOString());
          });

          // Count consecutive days going back from today
          const checkDate = new Date(today);
          while (checkinDays.has(checkDate.toISOString())) {
            streak++;
            checkDate.setDate(checkDate.getDate() - 1);
          }
        }

        // Get friend count (simple query)
        const { count: friendCount } = await supabase
          .from('friendships')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);

        // Calculate points (10 per check-in + 5 bonus per unique place)
        const points = (totalCheckins || 0) * 10 + uniquePlaces * 5;

        setStats({
          checkins: totalCheckins || 0,
          places: uniquePlaces,
          friends: friendCount || 0,
          streak,
          points,
          mayorships: 0, // TODO: Implement mayorships
          weeklyCheckins: weeklyCount || 0,
        });

        // Calculate achievements based on real data
        const earnedAchievements: Achievement[] = [
          {
            id: '1',
            name: 'First Check-in',
            icon: '🎉',
            earned: (totalCheckins || 0) >= 1,
            description: 'Check in for the first time',
          },
          {
            id: '2',
            name: 'Regular',
            icon: '☕',
            earned: (totalCheckins || 0) >= 5,
            description: 'Check in 5 times',
          },
          {
            id: '3',
            name: 'Streak Starter',
            icon: '🔥',
            earned: streak >= 3,
            description: 'Get a 3-day streak',
          },
          {
            id: '4',
            name: 'Explorer',
            icon: '🗺️',
            earned: uniquePlaces >= 10,
            description: 'Visit 10 unique places',
          },
          {
            id: '5',
            name: 'Social Butterfly',
            icon: '🦋',
            earned: (friendCount || 0) >= 10,
            description: 'Make 10 friends',
          },
          {
            id: '6',
            name: 'Power User',
            icon: '⚡',
            earned: (totalCheckins || 0) >= 25,
            description: '25 total check-ins',
          },
          {
            id: '7',
            name: 'Week Warrior',
            icon: '🏆',
            earned: (weeklyCount || 0) >= 7,
            description: 'Check in 7 times in one week',
          },
          {
            id: '8',
            name: 'Centurion',
            icon: '💯',
            earned: (totalCheckins || 0) >= 100,
            description: '100 total check-ins',
          },
        ];

        setAchievements(earnedAchievements);
      } catch (error) {
        console.error('Error fetching user stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserStats();
  }, [user?.id]);

  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Anonymous';
  const avatarUrl =
    profileAvatarUrl ||
    user?.user_metadata?.avatar_url ||
    `https://api.dicebear.com/7.x/avataaars/svg?seed=${displayName}`;
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarClick = () => {
    if (!user?.id) return;
    avatarInputRef.current?.click();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;
    if (!file.type.startsWith('image/')) {
      alert('Please choose an image file (e.g. JPG or PNG).');
      return;
    }
    e.target.value = '';
    setUploadingAvatar(true);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `${user.id}/avatar.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path);
      const { error: updateError } = await supabase
        .from('users')
        .update({ avatar_url: urlData.publicUrl })
        .eq('id', user.id);
      if (updateError) throw updateError;
      await refreshProfile();
    } catch (err: any) {
      console.error('Avatar upload failed:', err);
      alert(err?.message || 'Failed to update photo. Create an "avatars" storage bucket in Supabase with public read.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    onClose();
  };

  // Show Settings page
  if (showSettings) {
    return <SettingsPage onClose={() => setShowSettings(false)} />;
  }

  // Show Friends page
  if (showFriends) {
    return <FriendsPageSimple onClose={() => setShowFriends(false)} />;
  }

  // Show Saved Venues page
  if (showSaved) {
    return <SavedVenues onClose={() => setShowSaved(false)} />;
  }

  return (
    <div className="fixed inset-0 bg-[#c5ccd4] z-50 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="flex-shrink-0 bg-gradient-to-b from-[#6d84a3] via-[#5a7493] to-[#4d6580] border-b-2 border-gray-800 px-4 py-3 shadow-lg">
        <div className="flex items-center justify-between">
          <button
            onClick={onClose}
            className="text-white font-medium text-sm"
            style={{ textShadow: '0 -1px 0 rgba(0,0,0,0.5)' }}
          >
            ← Back
          </button>
          <h1
            className="text-lg font-bold text-white"
            style={{ textShadow: '0 -1px 0 rgba(0,0,0,0.5)' }}
          >
            Profile
          </h1>
          <button
            onClick={handleSignOut}
            className="p-2 bg-gradient-to-b from-gray-200 to-gray-300 rounded-lg shadow-md border border-gray-400"
          >
            <LogOut size={16} className="text-gray-700" />
          </button>
        </div>
      </header>

      {/* Profile Card */}
      <div className="flex-shrink-0 px-4 py-6">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-300 p-6">
          <div className="flex items-center gap-4">
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleAvatarChange}
            />
            <button
              type="button"
              onClick={handleAvatarClick}
              disabled={!user?.id || uploadingAvatar}
              className="relative rounded-full focus:ring-2 focus:ring-[#5ba4e5] focus:ring-offset-2"
            >
              <img
                src={avatarUrl}
                alt={displayName}
                className="w-20 h-20 rounded-full border-4 border-[#5ba4e5] shadow-lg object-cover"
              />
              {uploadingAvatar && (
                <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
                  <Loader2 size={24} className="text-white animate-spin" />
                </div>
              )}
              {user?.id && !uploadingAvatar && (
                <span className="absolute bottom-0 right-0 text-[10px] bg-[#5ba4e5] text-white px-1.5 py-0.5 rounded shadow">
                  Edit
                </span>
              )}
            </button>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">{displayName}</h2>
              <p className="text-sm text-gray-600">{user?.email || 'Guest User'}</p>
              <div className="flex items-center gap-2 mt-2">
                {loading ? (
                  <Loader2 size={16} className="animate-spin text-gray-400" />
                ) : (
                  <>
                    <span className="flex items-center gap-1 text-xs bg-gradient-to-b from-orange-100 to-orange-200 text-orange-700 px-2 py-1 rounded-full border border-orange-300">
                      <Flame size={12} />
                      {stats.streak} day streak
                    </span>
                    <span className="flex items-center gap-1 text-xs bg-gradient-to-b from-yellow-100 to-yellow-200 text-yellow-700 px-2 py-1 rounded-full border border-yellow-300">
                      <Star size={12} />
                      {stats.points} pts
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-3 mt-6">
            <div className="text-center p-3 bg-gradient-to-b from-gray-50 to-gray-100 rounded-xl border border-gray-200">
              {loading ? (
                <Loader2 size={20} className="mx-auto animate-spin text-gray-400" />
              ) : (
                <p className="text-2xl font-bold text-gray-900">{stats.checkins}</p>
              )}
              <p className="text-xs text-gray-600">Check-ins</p>
            </div>
            <div className="text-center p-3 bg-gradient-to-b from-gray-50 to-gray-100 rounded-xl border border-gray-200">
              {loading ? (
                <Loader2 size={20} className="mx-auto animate-spin text-gray-400" />
              ) : (
                <p className="text-2xl font-bold text-gray-900">{stats.places}</p>
              )}
              <p className="text-xs text-gray-600">Places</p>
            </div>
            <button
              onClick={() => setShowFriends(true)}
              className="text-center p-3 bg-gradient-to-b from-gray-50 to-gray-100 rounded-xl border border-gray-200 hover:border-blue-300 transition-colors"
            >
              {loading ? (
                <Loader2 size={20} className="mx-auto animate-spin text-gray-400" />
              ) : (
                <p className="text-2xl font-bold text-gray-900">{stats.friends}</p>
              )}
              <p className="text-xs text-gray-600">Friends</p>
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex-shrink-0 px-4">
        <div className="flex bg-white rounded-xl shadow border border-gray-300 p-1">
          {[
            { id: 'stats', label: 'Stats', icon: TrendingUp },
            { id: 'saved', label: 'Saved', icon: Bookmark },
            { id: 'achievements', label: 'Badges', icon: Award },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  if (tab.id === 'saved') {
                    setShowSaved(true);
                  } else {
                    setActiveTab(tab.id as typeof activeTab);
                  }
                }}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-bold transition-all ${
                  isActive
                    ? 'bg-gradient-to-b from-[#5ba4e5] to-[#3b7fc4] text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {activeTab === 'stats' && (
          <div className="space-y-3">
            <div className="bg-white rounded-xl shadow border border-gray-300 overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-bold text-gray-900">This Week</h3>
              </div>
              <div className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-b from-blue-100 to-blue-200 rounded-full flex items-center justify-center">
                      <MapPin size={18} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Check-ins</p>
                      <p className="text-xs text-gray-500">This week</p>
                    </div>
                  </div>
                  {loading ? (
                    <Loader2 size={20} className="animate-spin text-gray-400" />
                  ) : (
                    <p className="text-xl font-bold text-gray-900">{stats.weeklyCheckins}</p>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-b from-green-100 to-green-200 rounded-full flex items-center justify-center">
                      <Calendar size={18} className="text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Current Streak</p>
                      <p className="text-xs text-gray-500">Days in a row</p>
                    </div>
                  </div>
                  <p className="text-xl font-bold text-orange-500">🔥 {stats.streak}</p>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-b from-purple-100 to-purple-200 rounded-full flex items-center justify-center">
                      <Award size={18} className="text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Mayorships</p>
                      <p className="text-xs text-gray-500">Venues you rule</p>
                    </div>
                  </div>
                  <p className="text-xl font-bold text-gray-900">👑 {stats.mayorships}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'achievements' && (
          <div className="grid grid-cols-2 gap-3">
            {achievements.map((achievement) => (
              <div
                key={achievement.id}
                className={`p-4 rounded-xl border ${
                  achievement.earned
                    ? 'bg-white border-gray-300 shadow'
                    : 'bg-gray-100 border-gray-200 opacity-60'
                }`}
              >
                <div className="text-3xl mb-2">{achievement.icon}</div>
                <p
                  className={`font-bold text-sm ${
                    achievement.earned ? 'text-gray-900' : 'text-gray-500'
                  }`}
                >
                  {achievement.name}
                </p>
                <p className="text-xs text-gray-500 mt-1">{achievement.description}</p>
                {achievement.earned && (
                  <span className="inline-block mt-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                    ✓ Earned
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Settings Button */}
      <div className="flex-shrink-0 p-4 border-t border-gray-300 bg-white">
        <button
          onClick={() => setShowSettings(true)}
          className="w-full flex items-center justify-between p-4 bg-gradient-to-b from-gray-50 to-gray-100 rounded-xl border border-gray-300 active:bg-gray-200 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Settings size={20} className="text-gray-600" />
            <span className="font-medium text-gray-900">Settings</span>
          </div>
          <ChevronRight size={18} className="text-gray-400" />
        </button>
      </div>
    </div>
  );
}
